import type {
  TopologyFaceBoundarySelectionResolveInput,
  TopologyFaceBoundarySelectionServiceDependencies,
} from '../../../../../types/reference/sketch-entity-reference/TopologyFaceBoundarySelectionService';
import type {
  TopologyFaceReferenceSourceDescriptor,
} from '../../../../../types/reference/sketch-entity-reference/TopologyFaceReferenceSource';
import type {
  TopologyWireRuntimeCandidate,
} from '../../../../../types/reference/sketch-entity-reference/TopologyFaceBoundaryRuntime';
import {
  resolveExternalGeometrySourceDescriptorKey,
  resolveTopologySourceOwnerIdentityKey,
} from '../../model/ExternalGeometrySourceDescriptor';
import type {
  SketchEntityReferenceSelectionCandidate,
  SketchEntityReferenceSelectionCandidateSource,
} from './SketchEntityReferenceSelectionAdapter';
import {
  TopologySelectionRuntimeSourceSnapshotFactory,
} from '../../source/topology/TopologySelectionRuntimeSourceSnapshotFactory';

/**
 * Convert Entities topology 单边、面外环与 inner Wire 选择服务。
 *
 * 基础 resolver 负责识别用户命中的基础候选；canonical 单边查询、Face/Wire 重定位和稳定边来源构造
 * 统一交给运行时边界服务。本类只负责把完整运行时边界映射为面或环选择意图。
 */
export class TopologyFaceBoundarySelectionService {
  /** 完整运行时候选到纯数据命令快照的唯一收口工厂。 */
  private readonly runtimeSourceSnapshotFactory =
    new TopologySelectionRuntimeSourceSnapshotFactory();

  /**
   * @param dependencies 基础候选解析器与共享运行时边界服务。
   */
  public constructor(
    private readonly dependencies: TopologyFaceBoundarySelectionServiceDependencies,
  ) {}

  /**
   * 解析一次 Convert Entities 选择。
   *
   * @param input 命中对象、目标草图和当前选择模式。
   * @returns 面、环或基础单边/草图候选。
   */
  public async resolve(
    input: TopologyFaceBoundarySelectionResolveInput,
  ): Promise<SketchEntityReferenceSelectionCandidate | null> {
    const baseCandidate = this.dependencies.resolveBaseCandidate(
      input.targetObject,
      input.targetSketchId,
      input.options,
    );
    if (!baseCandidate) {
      return null;
    }
    if (
      baseCandidate.intent?.intentKind === 'topology-face'
      && baseCandidate.intent.topologyFaceSource
    ) {
      return this.resolveFaceCandidate(
        baseCandidate,
        baseCandidate.intent.topologyFaceSource,
      );
    }
    if (baseCandidate.descriptor.kind === 'topology-edge') {
      const edgeRuntime = await this.dependencies.boundaryRuntime.resolveEdgeSelection(
        baseCandidate.descriptor,
      );
      if (!edgeRuntime) {
        return null;
      }
      if (input.options?.innerLoopsOneByOne && edgeRuntime.innerLoop) {
        return this.createLoopCandidate({
          faceSource: edgeRuntime.innerLoop.faceSource,
          wireIndex: edgeRuntime.innerLoop.innerWireIndex,
          wire: edgeRuntime.innerLoop.innerWire,
        });
      }
      return {
        ...baseCandidate,
        descriptor: edgeRuntime.edgeSource,
        runtimeTopologySource: this.runtimeSourceSnapshotFactory.create(
          edgeRuntime.edgeCandidate,
        ),
      };
    }
    return baseCandidate;
  }

  /**
   * 为一组已选 Face 构建所有 inner Wire 候选。
   *
   * 任一 Face 查询或成员解析失败都会抛错，调用方据此保持批量 mutation 原子性。
   *
   * @param faceSources 当前已选 topology face。
   * @returns 每条 inner Wire 对应的一条 loop 候选。
   */
  public async resolveAllInnerLoops(
    faceSources: readonly TopologyFaceReferenceSourceDescriptor[],
  ): Promise<SketchEntityReferenceSelectionCandidate[]> {
    const candidates: SketchEntityReferenceSelectionCandidate[] = [];
    for (const faceSource of faceSources) {
      const runtime = await this.dependencies.boundaryRuntime.resolve(
        faceSource,
        undefined,
        undefined,
        'all-wires',
      );
      if (!runtime) {
        throw new Error(`Convert Entities 无法解析面 ${faceSource.locator.faceIndex + 1} 的内环`);
      }
      runtime.innerWires.forEach((wire, wireIndex) => {
        candidates.push(this.createLoopCandidate({
          faceSource: runtime.faceSource,
          wireIndex,
          wire,
        }));
      });
    }
    return candidates;
  }

  /**
   * 把基础面候选裁剪为 Worker 确认的 outer Wire。
   */
  private async resolveFaceCandidate(
    candidate: SketchEntityReferenceSelectionCandidate,
    faceSource: TopologyFaceReferenceSourceDescriptor,
  ): Promise<SketchEntityReferenceSelectionCandidate> {
    const runtime = await this.dependencies.boundaryRuntime.resolve(faceSource);
    if (!runtime) {
      throw new Error(`Convert Entities 无法解析面 ${faceSource.locator.faceIndex + 1} 的外环`);
    }
    const relatedSources = this.createRelatedSources(runtime.outerWire);
    return {
      ...candidate,
      descriptor: relatedSources[0]!.descriptor,
      intent: candidate.intent
          ? {
            ...candidate.intent,
            topologyFaceSource: runtime.faceSource,
          }
        : undefined,
      // 内环数量来自同一次权威内核 Shape 查询，避免 UI 为按钮状态重复猜测或查询。
      innerLoopCount: runtime.innerWireCount,
      relatedSources,
    };
  }

  /**
   * 创建一条完整 topology-loop 候选。
   */
  private createLoopCandidate(input: {
    readonly faceSource: TopologyFaceReferenceSourceDescriptor;
    readonly wireIndex: number;
    readonly wire: TopologyWireRuntimeCandidate;
  }): SketchEntityReferenceSelectionCandidate {
    const relatedSources = this.createRelatedSources(input.wire);
    const sourceKeys = relatedSources
      .map(source => resolveExternalGeometrySourceDescriptorKey(source.descriptor))
      .sort();
    const faceIdentity = input.faceSource.locator.subEntityId
      ?? input.faceSource.locator.persistentRef
      ?? `face-index:${input.faceSource.locator.faceIndex}`;
    return {
      descriptor: relatedSources[0]!.descriptor,
      displayLabel: `内环${input.wireIndex + 1}`,
      sourceObject: relatedSources[0]!.sourceObject,
      intent: {
        intentKind: 'topology-loop',
        intentId: [
          'topology-loop',
          resolveTopologySourceOwnerIdentityKey(input.faceSource.owner),
          faceIdentity,
          sourceKeys.join('|'),
        ].join('::'),
        displayKind: 'loop',
        displayLabel: `内环${input.wireIndex + 1}`,
        topologyFaceSource: structuredClone(input.faceSource),
      },
      relatedSources,
    };
  }

  /**
   * 把 RuntimeService 的完整 Wire 映射为选择候选来源。
   *
   * @param wire 已完成边完整性校验的当前 Wire。
   * @returns 与 edgeSources 顺序一致的选择来源。
   */
  private createRelatedSources(
    wire: TopologyWireRuntimeCandidate,
  ): SketchEntityReferenceSelectionCandidateSource[] {
    return wire.edgeSources.map((descriptor, sourceIndex) => ({
      descriptor,
      displayLabel: `边线${descriptor.locator.edgeIndex + 1}`,
      sourceObject: wire.edgeCandidates[sourceIndex]?.featureLine ?? undefined,
      runtimeTopologySource: this.runtimeSourceSnapshotFactory.create(
        wire.edgeCandidates[sourceIndex]!,
      ),
    }));
  }
}
